import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const planDetails = {
  trial: { nombre: 'Trial', precio: 0, precioOriginal: 0, color: 'gray', animales: '500', establecimientos: '1', desc: '30 dias de prueba gratuita' },
  basico: { nombre: 'Basico', precio: 9.99, precioOriginal: 19.99, color: 'green', animales: '1,000', establecimientos: '1', desc: 'Productores pequenos' },
  profesional: { nombre: 'Profesional', precio: 24.99, precioOriginal: 49.99, color: 'blue', animales: '3,500', establecimientos: '5', desc: 'Productores medianos y consultores' },
  enterprise: { nombre: 'Plus', precio: 49.99, precioOriginal: 99.99, color: 'purple', animales: '12,000', establecimientos: 'Ilimitado', desc: 'Grandes empresas ganaderas' },
  corporativo: { nombre: 'Enterprise', precio: null, precioOriginal: null, color: 'amber', animales: 'Ilimitado', establecimientos: 'Ilimitado', desc: 'Grupos ganaderos, pools de inversion, cooperativas — consultar cotizacion' },
};

const colorMap = {
  gray: { card: 'border-gray-300', badge: 'bg-gray-100 text-gray-700', btn: 'bg-gray-600 hover:bg-gray-700', bar: 'bg-gray-400' },
  green: { card: 'border-green-400', badge: 'bg-green-100 text-green-800', btn: 'bg-green-600 hover:bg-green-700', bar: 'bg-green-500' },
  blue: { card: 'border-blue-400', badge: 'bg-blue-100 text-blue-800', btn: 'bg-blue-600 hover:bg-blue-700', bar: 'bg-blue-500' },
  purple: { card: 'border-purple-400', badge: 'bg-purple-100 text-purple-800', btn: 'bg-purple-600 hover:bg-purple-700', bar: 'bg-purple-500' },
  amber: { card: 'border-amber-400', badge: 'bg-amber-100 text-amber-800', btn: 'bg-amber-600 hover:bg-amber-700', bar: 'bg-amber-500' },
};

const statusLabels = {
  trial: { text: 'TRIAL', bg: 'bg-amber-100 text-amber-800' },
  active: { text: 'ACTIVO', bg: 'bg-green-100 text-green-800' },
  expired: { text: 'EXPIRADO', bg: 'bg-red-100 text-red-800' },
};

const PlanesPage = () => {
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [paypalPlans, setPaypalPlans] = useState({});
  const [plansConfigured, setPlansConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupLoading, setSetupLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [payments, setPayments] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Get plan info via check-limits (finds client through establishment)
      const limRes = await api.get(`/admin/check-limits/${user?.establecimiento_id}`);
      setCurrentPlan(limRes.data);

      const ppRes = await api.get('/paypal/plans');
      setPaypalPlans(ppRes.data?.plans || {});
      setPlansConfigured(ppRes.data?.configured || false);

      if (limRes.data?.cliente_id) {
        const payRes = await api.get(`/paypal/payments/${limRes.data.cliente_id}`);
        setPayments(payRes.data?.payments || []);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') === 'true') {
      setMessage({ type: 'warning', text: 'Suscripcion cancelada. No se realizaron cambios.' });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    let subId = params.get('subscription_id');
    if (!subId) {
      subId = localStorage.getItem('pending_subscription_id');
      if (subId) localStorage.removeItem('pending_subscription_id');
    }
    if (subId) {
      activateSubscription(subId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const activateSubscription = async (subscriptionId) => {
    try {
      setMessage({ type: 'info', text: 'Activando suscripcion...' });
      const res = await api.get(`/paypal/activate/${subscriptionId}`);
      if (res.data.status === 'ACTIVE') {
        setMessage({ type: 'success', text: `Plan ${res.data.plan} activado exitosamente.` });
      } else {
        setMessage({ type: 'warning', text: `Estado: ${res.data.status}. Puede tardar unos minutos.` });
      }
      loadData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Error activando: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleSetupPlans = async () => {
    setSetupLoading(true);
    try {
      await api.post('/paypal/setup-plans');
      setMessage({ type: 'success', text: 'Planes PayPal configurados exitosamente' });
      loadData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + (err.response?.data?.detail || err.message) });
    } finally { setSetupLoading(false); }
  };

  const handleSubscribe = async (planKey) => {
    if (!currentPlan?.cliente_id) return;
    try {
      const baseUrl = window.location.origin;
      const res = await api.post('/paypal/subscribe', {
        plan_key: planKey,
        cliente_id: currentPlan.cliente_id,
        return_url: `${baseUrl}/planes`,
        cancel_url: `${baseUrl}/planes?cancelled=true`,
      });
      if (res.data.approve_url && res.data.subscription_id) {
        localStorage.setItem('pending_subscription_id', res.data.subscription_id);
        window.location.href = res.data.approve_url;
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleCancel = async () => {
    if (!currentPlan?.cliente_id || !window.confirm('Cancelar suscripcion? Tu plan volvera a Trial.')) return;
    try {
      await api.post(`/paypal/cancel/${currentPlan.cliente_id}`);
      setMessage({ type: 'success', text: 'Suscripcion cancelada.' });
      loadData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Error: ' + (err.response?.data?.detail || err.message) });
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const isAdmin = user?.rol === 'admin';
  const currentPlanKey = currentPlan?.plan || 'trial';
  const uso = currentPlan?.uso || {};
  const limites = currentPlan?.limites || { animales: 500, establecimientos: 1 };
  const pctAnimales = currentPlan?.porcentaje_animales || 0;
  const status = currentPlan?.subscription_status || 'trial';
  const stLabel = statusLabels[status] || statusLabels.trial;

  // Calculate trial days remaining
  let trialDays = null;
  if (status === 'trial' && currentPlan?.fecha_expiracion) {
    const exp = new Date(currentPlan.fecha_expiracion);
    const now = new Date();
    trialDays = Math.max(0, Math.ceil((exp - now) / (1000 * 60 * 60 * 24)));
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="planes-page">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Mi Plan</h2>
        <p className="text-sm text-gray-500 mt-1">Administra tu suscripcion de TKorveX</p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg text-sm font-medium ${
          message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          message.type === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`} data-testid="plan-message">
          {message.text}
          <button onClick={() => setMessage(null)} className="float-right font-bold">&times;</button>
        </div>
      )}

      {/* Current plan + usage */}
      {currentPlan && (
        <div className="bg-white rounded-xl shadow-sm p-5 border" data-testid="current-plan-card">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm text-gray-500">Plan actual</p>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${stLabel.bg}`} data-testid="subscription-status">{stLabel.text}</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {planDetails[currentPlanKey]?.nombre || currentPlanKey.charAt(0).toUpperCase() + currentPlanKey.slice(1)}
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-semibold ${colorMap[planDetails[currentPlanKey]?.color || 'gray']?.badge || 'bg-gray-100 text-gray-700'}`}>
                  {currentPlanKey === 'trial' ? 'GRATIS' : planDetails[currentPlanKey]?.precio ? `$${planDetails[currentPlanKey].precio}/mes` : 'Personalizado'}
                </span>
              </p>
              {trialDays !== null && (
                <p className={`text-sm mt-1 font-medium ${trialDays <= 3 ? 'text-red-600' : 'text-amber-600'}`} data-testid="trial-days-remaining">
                  {trialDays > 0 ? `${trialDays} dias restantes de prueba` : 'Tu trial ha expirado. Suscribite para continuar.'}
                </p>
              )}
              {currentPlan?.fecha_inicio_plan && (
                <p className="text-xs text-gray-400 mt-1">Desde: {new Date(currentPlan.fecha_inicio_plan).toLocaleDateString('es-AR')}</p>
              )}

              {/* Usage bar */}
              <div className="mt-4 space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Animales</span>
                    <span className={`font-semibold ${pctAnimales >= 90 ? 'text-red-600' : 'text-gray-900'}`} data-testid="animal-usage">
                      {uso.animales || 0} / {limites.animales === -1 ? 'Ilimitado' : limites.animales?.toLocaleString()}
                    </span>
                  </div>
                  {limites.animales > 0 && (
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className={`h-2.5 rounded-full transition-all ${pctAnimales >= 90 ? 'bg-red-500' : pctAnimales >= 70 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(pctAnimales, 100)}%` }} data-testid="animal-usage-bar" />
                    </div>
                  )}
                  {pctAnimales >= 90 && limites.animales > 0 && (
                    <p className="text-xs text-red-600 mt-1 font-medium" data-testid="warning-90">
                      Estas al {pctAnimales}% de tu limite. Considera actualizar tu plan.
                    </p>
                  )}
                </div>
                <div className="flex gap-6 text-sm text-gray-600">
                  <span>Establecimientos: <strong>{uso.establecimientos || 0} / {limites.establecimientos === -1 ? 'Ilimitado' : limites.establecimientos}</strong></span>
                  <span>Usuarios: <strong>Ilimitado</strong></span>
                </div>
                <div className="flex gap-4 text-sm mt-2">
                  <span className="flex items-center gap-1 text-green-700 font-medium">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    IA Asistente
                  </span>
                  <span className="flex items-center gap-1 text-green-700 font-medium">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Alertas automaticas IA
                  </span>
                  <span className="flex items-center gap-1 text-green-700 font-medium">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Informes automaticos
                  </span>
                </div>
              </div>
            </div>
            {currentPlanKey !== 'trial' && currentPlan.paypal_subscription_id && (
              <button onClick={handleCancel} className="px-4 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 flex-shrink-0" data-testid="cancel-subscription-btn">
                Cancelar Suscripcion
              </button>
            )}
          </div>
        </div>
      )}

      {/* Admin setup */}
      {isAdmin && !plansConfigured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <p className="text-sm text-amber-800 mb-3 font-medium">Planes PayPal no configurados.</p>
          <button onClick={handleSetupPlans} disabled={setupLoading} className="px-5 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50" data-testid="setup-plans-btn">
            {setupLoading ? 'Configurando...' : 'Configurar Planes PayPal'}
          </button>
        </div>
      )}

      {/* Plan cards */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-bold text-gray-900">Planes disponibles</h3>
          <span className="px-3 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full animate-pulse" data-testid="launch-badge">
            PRECIO DE LANZAMIENTO
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {['basico', 'profesional', 'enterprise', 'corporativo'].map(key => {
            const plan = planDetails[key];
            const colors = colorMap[plan.color];
            const isCurrent = currentPlanKey === key;
            const isUpgrade = key !== 'corporativo' && (planDetails[currentPlanKey]?.precio || 0) < (plan.precio || 0);
            const ppPlan = paypalPlans[key];

            return (
              <div key={key} className={`rounded-xl border-2 p-6 bg-white transition relative overflow-hidden ${colors.card} ${isCurrent ? 'ring-2 ring-green-500 shadow-lg' : 'hover:shadow-md'}`} data-testid={`plan-card-${key}`}>
                {/* Launch badge ribbon */}
                {key !== 'corporativo' && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                      -{Math.round((1 - plan.precio / plan.precioOriginal) * 100)}%
                    </span>
                  </div>
                )}
                {isCurrent && <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full font-semibold mb-2 inline-block">PLAN ACTUAL</span>}
                {key === 'basico' && currentPlanKey === 'trial' && (
                  <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold mb-2 inline-block ml-1" data-testid="trial-badge-basico">
                    30 DIAS GRATIS
                  </span>
                )}
                {key === 'profesional' && (
                  <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full font-semibold mb-2 inline-block" data-testid="popular-badge">
                    MAS POPULAR
                  </span>
                )}
                <h3 className="text-lg font-bold text-gray-900">{plan.nombre}</h3>

                {/* Price */}
                {plan.precio !== null ? (
                  <div className="mt-1 mb-1">
                    {plan.precioOriginal && plan.precioOriginal > plan.precio && (
                      <span className="text-sm text-gray-400 line-through mr-2" data-testid={`original-price-${key}`}>${plan.precioOriginal}</span>
                    )}
                    <span className="text-3xl font-bold text-gray-800">${plan.precio}</span>
                    <span className="text-sm text-gray-500 font-normal">/mes</span>
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-amber-700 mt-1 mb-1" data-testid="corporativo-price">A medida</p>
                )}

                <p className="text-xs text-gray-500 mb-5">{plan.desc}</p>
                <div className="space-y-2 text-sm text-gray-600 mb-6">
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Animales: <strong>{plan.animales}</strong>
                  </p>
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Establecimientos: <strong>{plan.establecimientos}</strong>
                  </p>
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Usuarios: <strong>Ilimitado</strong>
                  </p>
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    IA Asistente: <strong>Incluido</strong>
                  </p>
                  <p className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    Alertas automaticas IA: <strong>Incluido</strong>
                  </p>
                  {key === 'enterprise' && (
                    <p className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                      Reportes PDF automaticos
                    </p>
                  )}
                  {key === 'corporativo' && (
                    <>
                      <p className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        Soporte dedicado
                      </p>
                      <p className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        Integraciones a medida
                      </p>
                    </>
                  )}
                </div>

                {key === 'corporativo' ? (
                  <a href="mailto:contacto@tkorvex.com?subject=Consulta%20Plan%20Corporativo"
                    className={`block w-full py-2.5 px-4 text-center text-white rounded-lg text-sm font-semibold transition ${colors.btn}`}
                    data-testid="corporativo-contact-btn">
                    Consultar Presupuesto
                  </a>
                ) : isCurrent ? (
                  <div className="py-2.5 px-4 bg-green-50 rounded-lg text-green-700 text-sm font-medium text-center">Tu plan actual</div>
                ) : isUpgrade && plansConfigured && ppPlan ? (
                  <button onClick={() => handleSubscribe(key)} className={`w-full py-2.5 px-4 text-white rounded-lg text-sm font-semibold transition ${colors.btn}`} data-testid={`subscribe-btn-${key}`}>
                    {key === 'basico' && currentPlanKey === 'trial' ? 'Probar 30 dias gratis' : `Suscribirse — $${plan.precio}/mes`}
                  </button>
                ) : isUpgrade && !plansConfigured ? (
                  <p className="text-xs text-gray-400 text-center">Configura PayPal para habilitar</p>
                ) : (
                  <p className="text-xs text-gray-400 text-center">-</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment history */}
      {payments.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b"><h3 className="text-lg font-bold text-gray-900">Historial de Pagos</h3></div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="payments-table">
              <thead className="bg-gray-50">
                <tr>{['Fecha', 'Plan', 'Monto', 'Estado'].map(h => <th key={h} className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">{h}</th>)}</tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-3 px-4">{p.fecha ? new Date(p.fecha).toLocaleDateString('es-AR') : '--'}</td>
                    <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded text-xs font-semibold ${colorMap[planDetails[p.plan]?.color || 'gray']?.badge}`}>{(p.plan || '').toUpperCase()}</span></td>
                    <td className="py-3 px-4 font-semibold">${p.monto} {p.moneda}</td>
                    <td className="py-3 px-4"><span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">{p.estado}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanesPage;
